import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  sortAdminOrderedRows,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminFounderList,
  formatYowanAdminDateTime,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const FOUNDER_PAGE_SIZE = 30;
const founderSortOptions = [
  { value: 'display-order-asc', label: '表示順が小さい順' },
  { value: 'display-order-desc', label: '表示順が大きい順' },
  { value: 'published-status', label: '公開ステータス順' },
  { value: 'title-asc', label: 'タイトル昇順' },
  { value: 'title-desc', label: 'タイトル降順' },
];

const founderTitleColumnStyle = {
  width: '17rem',
  minWidth: '17rem',
};

const founderDisplayOrderColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};

const founderDateColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};
const founderActionColumnStyle = {
  width: '8.75rem',
  minWidth: '8.75rem',
  whiteSpace: 'nowrap',
};
const founderActionGroupStyle = {
  minWidth: '8.75rem',
  justifyContent: 'flex-start',
};

const AdminFounderListPage = () => {
  const content = yowanPageContent['/admin/content/founder'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('display-order-asc');
  const [currentPage, setCurrentPage] = useState(1);

  React.useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminFounderList()
      .then((payload) => {
        if (isMounted) {
          setRows(payload.results);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setLoadError('一覧を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!content) {
    return null;
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [row.title, row.body, row.isPublished ? '公開中' : '非公開', String(row.displayOrder)]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery)),
      )
    : rows;
  const sortedRows = sortAdminOrderedRows(filteredRows, sortKey);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / FOUNDER_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * FOUNDER_PAGE_SIZE;
  const visibleRows = sortedRows.slice(startIndex, startIndex + FOUNDER_PAGE_SIZE);

  return (
    <AdminBlogShell activeNavHref="/admin/content/founder" routeKey="/admin/content/founder">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${sortedRows.length}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="タイトル / 本文 / 表示順 / 公開"
        sortOptions={founderSortOptions}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={founderTitleColumnStyle}>タイトル</th>
              <th scope="col">公開</th>
              <th scope="col" style={founderDisplayOrderColumnStyle}>表示順</th>
              <th scope="col" style={founderDateColumnStyle}>作成日</th>
              <th scope="col" style={founderDateColumnStyle}>更新日</th>
              <th scope="col" style={founderActionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr key={row.id}>
                <td>{startIndex + index + 1}</td>
                <td style={founderTitleColumnStyle}>
                  <p className="yowan-admin-resource-table__title">{row.title}</p>
                </td>
                <td>
                  <span
                    className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${
                      row.isPublished ? 'published' : 'draft'
                    }`}
                  >
                    {row.isPublished ? '公開中' : '非公開'}
                  </span>
                </td>
                <td style={founderDisplayOrderColumnStyle}>{row.displayOrder}</td>
                <td style={founderDateColumnStyle}>{formatYowanAdminDateTime(row.createdAt)}</td>
                <td style={founderDateColumnStyle}>{formatYowanAdminDateTime(row.updatedAt)}</td>
                <td style={founderActionColumnStyle}>
                  <div className="yowan-admin-resource-table__actions" style={founderActionGroupStyle}>
                    <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                  </div>
                </td>
              </tr>
            ))}
            {!visibleRows.length ? (
              <tr>
                <td colSpan="7">
                  {isLoading ? <LoadingSpinner size="sm" /> : loadError || '表示できるレコードがありません。'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminFounderListPage;
